//Library Imports
import styled from 'styled-components';
import store, { RootState } from 'store';
import { useSelector } from 'react-redux';
import React, { useEffect, Suspense } from 'react';

//Local Imports
import { getQueryParam } from 'utils';
import { EMAIL_LIST } from 'constants/types';
import { PATH_FAVOURITES } from '../../../../constants/index';
import { getQueryCampaignId } from 'modules/EmailBrowser/utils';
import { getCampaignDetails, resetSentEmails } from '../actions';
import { updateCampaignName } from '../../CampaignAssets/actions';
import { datesFilterChange } from 'modules/Campaigns/CampaignContent/actions';

const EmailDashboard = React.lazy(() => import('./EmailDashboard'));
const StickyHeaderContentWithBackButton = React.lazy(() => import('modules/Campaigns/Shared/StickyHeaderContentWithBackButton'));
const AssetsTab = React.lazy(() => import('modules/Campaigns/Emails/Components/AssetsTab'));
const StickyHeader = React.lazy(() => import('modules/Common/Components/StickyHeader'));
const CampaignSmartSourcedContent = React.lazy(() => import('./SmartSourcesContent'));
const Audience = React.lazy(() => import('./Audience'));
const PreviouslySent = React.lazy(() => import('./PreviouslySent'));
const FavoritesTab = React.lazy(() => import('modules/Common/Components/FavoritesTab'));
const SentEditionsPage = React.lazy(() => import('./SentEditionsPage'));

interface Props {
  tab?: string;
  subTab?: string;
}

const EmailWrapper = (props: Props) => {
  const campaignId = getQueryCampaignId();
  const fromParam = getQueryParam('from');
  const campaignName = useSelector((state: RootState) => state.emails.campaignName);
  const reset: boolean = useSelector((state: RootState) => state.emails.reset);

  useEffect(() => {
    document.body.style.overflow = 'auto';
    window.scrollTo(0, 0);
    if (reset) {
      store.dispatch({
        type: EMAIL_LIST.SENT_LOAD,
      });
    }
    if (!campaignName || reset) getCampaignDetails();
    datesFilterChange({ value: 'qtr', label: '1 QTR' });
    return () => {};
  }, []);

  useEffect(() => {
    return () => {
      resetSentEmails();
    };
  }, [props.tab]);

  function campaignNameSubmitHandler(campaignName: string) {
    updateCampaignName(campaignId.toString(), campaignName, '');
  }

  const handleGoBackURL = () => {
    if (fromParam === 'forms') {
      return '/settings/forms';
    }
    return '/campaigns/';
  };

  return (
    <Wrapper>
      {/* intentionally adding overlayClass="asset-header" PLEASE DON'T REMOVE */}
      <Suspense fallback={<></>}>
        <StickyHeader overlayClass="asset-header">
          <StickyHeaderContentWithBackButton
            itemTitle={''}
            itemName={campaignName}
            loading={false}
            hideBack={false}
            editNameSubmitHandler={campaignNameSubmitHandler}
            nonEditable={false}
            firstColumnSpan={11}
            secondColumnSpan={11}
            goBackURL={handleGoBackURL()}
          >
            <Suspense fallback={<></>}>
              <AssetsTab assetType={'hybrid'} tab={props.tab} />
            </Suspense>
          </StickyHeaderContentWithBackButton>
        </StickyHeader>
      </Suspense>

      {props.tab === undefined || props.tab === '/' ? (
        <Suspense fallback={<></>}>
          <EmailDashboard />
        </Suspense>
      ) : props.tab === 'source' ? (
        <Suspense fallback={<></>}>
          <CampaignSmartSourcedContent isCampaign={true} />
        </Suspense>
      ) : props.tab === PATH_FAVOURITES ? (
        <Suspense fallback={<></>}>
          <FavoritesTab />
        </Suspense>
      ) : props.tab === 'audience' ? (
        <Suspense fallback={<></>}>
          <Audience />
        </Suspense>
      ) : props.tab === 'previous' ? (
        <Suspense fallback={<></>}>
          <PreviouslySent />
        </Suspense>
      ) : props.tab === 'sent' ? (
        <Suspense fallback={<></>}>
          <SentEditionsPage />
        </Suspense>
      ) : null}
    </Wrapper>
  );
};

export default EmailWrapper;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  .campaign-main-row {
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    .campaign-left-col {
      width: 72%;
      padding-top: 20px;
      padding-right: 60px;
      h4 {
        color: #001823;
        font-size: 26px;
        font-family: 'Roboto Slab', serif;
        margin: 10px 0 30px 0;
        padding: 0;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 18px;
        }
      }
      ul.select-list,
      ul.select-list ul {
        list-style-type: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        &.z-index-9 {
          div[class$='-container'] {
            z-index: 8 !important;
          }
        }
        li {
          margin: 5px 5px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            margin: 5px 3px;
          }
          /* div[class$='-control'] {
            width: 245px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              width: 210px;
            }
          } */
          .list-view {
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              min-width: 36px;
              width: 36px;
              height: 30px;
              border-top-left-radius: 6px;
              border-bottom-left-radius: 6px;
              img {
                width: 18px;
              }
            }
          }
          .card-view {
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              min-width: 36px;
              width: 36px;
              height: 30px;
              border-top-right-radius: 6px;
              border-bottom-right-radius: 6px;
              img {
                width: 18px;
              }
            }
          }
          h3 {
            font-size: 22px;
            color: #67696a;
            font-weight: 500;
            font-family: 'robotomedium';
            margin: 6px 10px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 16px;
            }
          }
          &.small-search {
            width: 320px;
            @media all and (max-width: 1439px) {
              width: 200px;
            }
          }
        }
      }
      .chart-container {
        width: 100%;
        height: inherit;
        max-height: 415px !important;
        > div {
          height: 415px !important;
          z-index: 7;
        }
      }
      .editions-heading {
        color: #67696a;
        font-weight: 500;
        font-family: 'Roboto';
      }
    }
    .campaign-right-col {
      width: 28%;
      padding: 100px 20px 0 0;
      background: rgb(238, 238, 238);
      background: linear-gradient(-63deg, rgba(238, 238, 238, 1) 0%, rgba(216, 216, 216, 1) 100%);
      #overallEngagment {
        margin-left: -45px;
        .engagement-chart-container {
          margin-bottom: 20px;
          height: 230px;
        }
      }
    }
  }
  .search-icon-container {
    cursor: pointer;
    padding: 5px 0 5px 25px;
    @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
      padding: 5px 0 5px 12px;
    }
  }
  .button-and-search-icon {
    align-items: inherit;
    display: flex;
    justify-content: space-between;
    padding: 1em 0 1.5em 5px;
    @media all and (max-width: 1439px), (max-height: 730px), (-webkit-device-pixel-ratio: 1.25) {
      padding-bottom: 12px;
    }
  }
  .sticky-header-tabs-container {
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  .heading-campaign {
    font-size: 22px;
    color: #67696a;
    font-weight: 500;
    font-family: 'Roboto', sans-serif;
    margin-bottom: 20px;
    margin-top: 50px;
  }
  h2 {
    color: #424b50;
    font-size: 40px;
    font-weight: 600;
    line-height: 47px;
    margin-bottom: 0px;
    display: block;
    font-family: 'Roboto Slab', serif;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 28px;
      line-height: 33px;
    }
  }
  .head-wrapper {
    display: flex;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .btn-wrapper {
      display: flex;
      align-items: center;
      .ml {
        margin-left: 12px;
      }
    }
  }
  .asset-name {
    text-overflow: ellipsis;
    display: block;
    overflow: hidden;
    white-space: nowrap;
    float: left;
    max-width: 700px;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      max-width: 445px;
    }
  }
  .name {
    color: rgb(155, 155, 155);
    position: relative;
    float: left;
    display: flex;
    align-items: center;

    .tooltip {
      position: relative;
      display: inline-block;
      opacity: 1;
      z-index: 0;
      .tooltiptext {
        color: #ffffff;
        font-size: 11px;
        letter-spacing: 0.02px;
        line-height: 15px;
        text-align: center;
        background-color: #263153;
        visibility: hidden;
        border-radius: 4px;
        padding: 5px 0;
        position: absolute;
        z-index: 1;
        bottom: 125%;
        left: 50%;
        transform: translateX(-50%);
        opacity: 0;
        transition: opacity 0.3s;
        padding: 5px 10px;
        &:after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          margin-left: -5px;
          border-width: 5px;
          border-style: solid;
          border-color: #263153 transparent transparent transparent;
        }
      }
      &:hover .tooltiptext {
        visibility: visible;
        opacity: 1;
      }
    }
    .asset-name {
      text-overflow: ellipsis;
      display: block;
      overflow: hidden;
      white-space: nowrap;
      float: left;
      max-width: 700px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        max-width: 445px;
      }
    }
    &.editable-active {
      .editable {
        display: block;
        min-width: 320px;
      }
    }
    .editable {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      display: none;
      label {
        display: none;
      }
      input {
        padding-right: 70px;
      }
      .save {
        position: absolute;
        top: 9px;
        right: 10px;
        height: 100%;
        cursor: pointer;
        img {
          vertical-align: middle;
          max-width: 20px;
        }
      }
      .cancel {
        position: absolute;
        top: 9px;
        right: 40px;
        height: 100%;
        cursor: pointer;
        img {
          vertical-align: middle;
          max-width: 20px;
        }
      }
    }
    .edit-btn {
      cursor: pointer;
      margin-left: 10px;
    }
  }
  .scroll-Wrapper {
    height: auto;
    overflow-x: hidden;
    @media all and (max-height: 780px) {
      max-height: 315px;
    }
    scrollbar-width: thin;
    scrollbar-color: #001760 #efefef;
    &::-webkit-scrollbar {
      display: none;
    }
    &::-webkit-scrollbar {
      width: 5px;
      height: 5px;
      border-radius: 5px;
    }
    /* Track */
    &::-webkit-scrollbar-track {
      background: #fff;
      margin-top: 10px;
      margin-bottom: 15px;
    }
    /* Handle */
    &::-webkit-scrollbar-thumb {
      background: #001760;
    }
    /* Handle on hover */
    &::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }

  .editions-text-styling {
    color: #67696a;
    font-size: 22px;
    font-weight: 700;
    letter-spacing: -0.46px;
    line-height: 34px;
    margin: 10px 0;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 16px;
      line-height: 24px;
    }
  }

  .rotate-arrow {
    margin-left: 0.25em;
    width: 15px;
    height: 12px;
  }

  div[aria-expanded='false'] {
    .rotate-arrow {
      transform: rotate(90deg);
    }
  }

  .add-words-wrapper {
    .wrapper-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      .add-word-head {
        color: #5e7079;
        font-size: 20px;
        margin-bottom: 0px;
        font-family: 'Roboto', sans-serif;
        font-weight: 600;
        display: inline-block;
        letter-spacing: -0.5px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 14px;
        }
      }
      .sub-text {
        font-size: 14px;
        margin-left: 3px;
        font-weight: 600;
        opacity: 0.4;
        color: rgba(0, 0, 0, 0.85);
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 10px;
        }
      }
      .test {
        font-weight: bold;
        font-size: 12px;
        color: #27ce0a;
        border: 1px solid #27ce0a;
        padding: 2px 8px;
        border-radius: 6px;
        cursor: pointer;
        letter-spacing: 1px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 9px;
          border-radius: 4px;
        }
      }
    }
    .search-input {
      padding: 10px 18px;
      font-size: 16px;
      line-height: 20px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
        line-height: 16px;
        padding: 5px 10px;
        border-radius: 6px;
      }
    }
    .dec-paragraph {
      color: #5e7079;
      font-size: 14px;
      margin-bottom: 0px;
      font-family: 'Roboto', sans-serif;
      font-weight: 600;
      margin-top: 10px;
      opacity: 0.6;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
      }
      &.mb {
        margin-bottom: 10px;
      }
    }
  }
  .horizontal-space-between {
    justify-content: space-between;
  }
  .more-less-button {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    min-height: 200px;
  }
  .campaign-dashboard-heading {
    color: #67696a !important;
    font-weight: 500 !important;
    font-family: 'Roboto' !important;
    font-size: 22px !important;
    margin: 0px !important;
    white-space: nowrap;
  }
  .campaign-dashboard-top-heading {
    font-size: 26px !important;
    line-height: 20px !important;
  }
  .margin-left-0 {
    margin-left: 0px !important;
  }
`;
